import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X, Check, Pen, RotateCcw, Send, Smartphone, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function InvoiceWarrantySignatureModal({ 
  isOpen, 
  onClose, 
  invoice, 
  onSignatureComplete 
}) {
  const [mode, setMode] = useState('choose'); // choose, on_site, remote
  const [isLoading, setIsLoading] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [remoteMethod, setRemoteMethod] = useState('sms'); // sms, email, both
  const [additionalEmails, setAdditionalEmails] = useState(''); // Emails supplémentaires séparés par virgule
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (mode === 'on_site' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [mode]);

  const startDrawing = (e) => {
    if (!canvasRef.current) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const handleSignOnSite = async () => {
    if (!signatureData) {
      toast.error('Veuillez signer avant de valider');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/invoices/${invoice.id}/warranty/sign`,
        {
          signature: signatureData,
          method: 'on_site'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { user_id: 'current' }
        }
      );
      toast.success('Signature enregistrée avec succès');
      onSignatureComplete?.();
      onClose();
    } catch (error) {
      console.error('Error signing:', error);
      toast.error('Erreur lors de l\'enregistrement de la signature');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestRemoteSignature = async () => {
    setIsLoading(true);
    try {
      // Parser les emails supplémentaires
      const extraEmails = additionalEmails
        .split(/[,;]/)
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));
      
      const response = await axios.post(
        `${BACKEND_URL}/api/invoices/${invoice.id}/warranty/request-signature`,
        { 
          method: remoteMethod,
          additional_emails: extraEmails.length > 0 ? extraEmails : undefined
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        const messages = [];
        if (response.data.sms_sent) messages.push('SMS envoyé');
        if (response.data.email_sent) messages.push('Email envoyé');
        if (response.data.additional_emails_sent) {
          messages.push(`${response.data.additional_emails_sent} email(s) supplémentaire(s)`);
        }
        toast.success(`Demande de signature envoyée: ${messages.join(', ')}`);
        onClose();
      }
    } catch (error) {
      console.error('Error requesting signature:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la demande');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Pen className="w-6 h-6 text-blue-600" />
            Signature Garantie - {invoice?.number || invoice?.invoice_number}
          </DialogTitle>
        </DialogHeader>

        {/* Choix du mode */}
        {mode === 'choose' && (
          <div className="space-y-6 py-4">
            <p className="text-slate-600">
              Choisissez le mode de signature pour les conditions de garantie :
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={() => setMode('on_site')}
                className="p-6 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <Pen className="w-10 h-10 text-blue-600 mb-3" />
                <h3 className="font-bold text-lg text-slate-800">Signature sur place</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Le client signe directement sur l'écran
                </p>
              </button>
              
              <button
                onClick={() => setMode('remote')}
                className="p-6 border-2 border-slate-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-left"
              >
                <Smartphone className="w-10 h-10 text-green-600 mb-3" />
                <h3 className="font-bold text-lg text-slate-800">Signature à distance</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Envoyer un lien avec code OTP par SMS/Email
                </p>
              </button>
            </div>
          </div>
        )}

        {/* Mode signature sur place */}
        {mode === 'on_site' && (
          <div className="space-y-4 py-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setMode('choose')}
              className="mb-2"
            >
              ← Retour au choix
            </Button>

            {/* Conditions de garantie */}
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 max-h-96 overflow-y-auto">
              <h3 className="font-bold text-blue-900 text-lg mb-4">
                📋 CAHIER DES CHARGES SAV & GARANTIES
              </h3>
              
              {/* 1. Référentiel des garanties */}
              <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
                <h4 className="font-bold text-green-800 text-sm mb-2">1. RÉFÉRENTIEL DES GARANTIES</h4>
                <ul className="text-xs text-green-700 space-y-1">
                  <li>• Appareils Neufs / Reconditionnés : <strong>24 mois</strong></li>
                  <li>• Pièces d'Origine (Service Pack) : <strong>12 mois</strong></li>
                  <li>• Compatibles Premium : <strong>6 mois</strong></li>
                  <li>• Entrée de Gamme : <strong>3 mois minimum</strong></li>
                </ul>
                <p className="text-xs text-green-600 mt-2">
                  ⚖️ Art. L217-11 : Immobilisation &gt; 7 jours = Extension automatique
                </p>
              </div>

              {/* 2. Ce qui est pris en charge */}
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mb-3">
                <h4 className="font-bold text-emerald-800 text-sm mb-2">2. CE QUI EST PRIS EN CHARGE</h4>
                <p className="text-xs text-emerald-700">
                  Vices de fabrication et défauts internes uniquement :
                  Affichage, Tactile, Batterie, Composants
                </p>
              </div>

              {/* 3. Ce qui est exclu */}
              <div className="bg-red-50 border-2 border-red-300 rounded p-3 mb-3">
                <h4 className="font-bold text-red-800 text-sm mb-2">3. CE QUI EST EXCLU (REJET)</h4>
                <ul className="text-xs text-red-600 space-y-1">
                  <li><strong>A. Écran :</strong> Lignes colorées, taches noires, fissure</li>
                  <li><strong>B. Physique :</strong> Vitre brisée, châssis tordu, coins enfoncés</li>
                  <li><strong>C. Intervention Tierce :</strong> Vis marquées, sticker rompu</li>
                  <li><strong>D. Oxydation :</strong> Pastilles rouges, corrosion</li>
                </ul>
              </div>

              {/* 4. Clauses juridiques */}
              <div className="bg-slate-100 border border-slate-300 rounded p-3 mb-3">
                <h4 className="font-bold text-slate-800 text-sm mb-2">4. CLAUSES JURIDIQUES</h4>
                <ul className="text-xs text-slate-700 space-y-1">
                  <li>📜 Exclusivité: Pièce DOIT rester montée</li>
                  <li>📜 Données: Client responsable de la sauvegarde</li>
                  <li>📜 Rétention: Appareil conservé jusqu'au paiement</li>
                </ul>
              </div>

              {/* 5. Logique système */}
              <div className="bg-purple-50 border border-purple-200 rounded p-3">
                <h4 className="font-bold text-purple-800 text-sm mb-2">5. LOGIQUE SYSTÈME</h4>
                <ul className="text-xs text-purple-700 space-y-1">
                  <li>📸 3 photos obligatoires (Face, Dos, Profil)</li>
                  <li>✍️ Signature obligatoire à chaque étape</li>
                </ul>
              </div>
            </div>

            {/* Zone de signature */}
            <div className="border-2 border-slate-300 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-slate-800">✍️ Signature du client</h4>
                <Button variant="outline" size="sm" onClick={clearCanvas}>
                  <RotateCcw className="w-4 h-4 mr-1" /> Effacer
                </Button>
              </div>
              
              <canvas
                ref={canvasRef}
                width={600}
                height={150}
                className="w-full border border-slate-300 rounded cursor-crosshair bg-white touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              
              <p className="text-xs text-slate-500 mt-2 text-center">
                En signant, le client confirme avoir lu et accepté les conditions de garantie ci-dessus.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button 
                onClick={handleSignOnSite}
                disabled={!signatureData || isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading ? 'Enregistrement...' : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Valider la signature
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Mode signature à distance */}
        {mode === 'remote' && (
          <div className="space-y-4 py-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setMode('choose')}
              className="mb-2"
            >
              ← Retour au choix
            </Button>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">Signature à distance avec OTP</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Un lien de signature et un code OTP seront envoyés au client.
                    Le client devra saisir le code pour valider son identité avant de signer.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Informations client
                </label>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-sm"><strong>Nom:</strong> {invoice?.client_name}</p>
                  <p className="text-sm"><strong>Téléphone:</strong> {invoice?.client_phone || 'Non renseigné'}</p>
                  <p className="text-sm"><strong>Email:</strong> {invoice?.client_email || 'Non renseigné'}</p>
                </div>
              </div>

              {/* Emails supplémentaires */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Emails supplémentaires (optionnel)
                </label>
                <input
                  type="text"
                  value={additionalEmails}
                  onChange={(e) => setAdditionalEmails(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Séparez les emails par des virgules pour envoyer à plusieurs destinataires
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Méthode d'envoi
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setRemoteMethod('sms')}
                    className={`flex-1 p-3 border-2 rounded-lg flex items-center justify-center gap-2 transition-all ${
                      remoteMethod === 'sms' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    disabled={!invoice?.client_phone}
                  >
                    <Smartphone className="w-5 h-5" />
                    SMS
                  </button>
                  <button
                    onClick={() => setRemoteMethod('email')}
                    className={`flex-1 p-3 border-2 rounded-lg flex items-center justify-center gap-2 transition-all ${
                      remoteMethod === 'email' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    disabled={!invoice?.client_email}
                  >
                    <Mail className="w-5 h-5" />
                    Email
                  </button>
                  <button
                    onClick={() => setRemoteMethod('both')}
                    className={`flex-1 p-3 border-2 rounded-lg flex items-center justify-center gap-2 transition-all ${
                      remoteMethod === 'both' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    disabled={!invoice?.client_phone || !invoice?.client_email}
                  >
                    <Send className="w-5 h-5" />
                    Les deux
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4">
              <Button variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button 
                onClick={handleRequestRemoteSignature}
                disabled={isLoading || (!invoice?.client_phone && !invoice?.client_email)}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading ? 'Envoi en cours...' : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer la demande
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
